import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import type { ApprovalFieldDiff } from '@/api/hooks/useApprovalRequests';

/**
 * The reviewer's side-by-side comparison: what the value is now, and what it
 * would become.
 *
 * An approver deciding from a summary line ("EPC change requested") is
 * rubber-stamping. Showing the concrete before and after is the difference
 * between a control and a formality.
 */
export function ApprovalDiffTable({ diffs }: { diffs: ApprovalFieldDiff[] }) {
  const { t } = useTranslation('approvals');

  if (diffs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{t('detail.noFieldChanges')}</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-3 py-2 text-left font-medium">{t('detail.field')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('detail.currentValue')}</th>
            <th className="w-8" />
            <th className="px-3 py-2 text-left font-medium">{t('detail.proposedValue')}</th>
          </tr>
        </thead>
        <tbody>
          {diffs.map((diff) => (
            <tr key={diff.field} className="border-t">
              <td className="px-3 py-2 font-medium">{diff.field}</td>
              <td className="px-3 py-2 font-mono text-muted-foreground line-through decoration-muted-foreground/50">
                {diff.oldValue ?? t('detail.empty')}
              </td>
              <td className="px-1 py-2 text-muted-foreground">
                <ArrowRight className="h-4 w-4" />
              </td>
              <td className="px-3 py-2 font-mono font-medium text-emerald-600 dark:text-emerald-400">
                {diff.newValue ?? t('detail.empty')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
